import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { Maybe } from '@/helpers/types';
import type { TrackingSpecs } from '@/libraries/tracking/specTracker';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

import { createSpecTracker } from '@/libraries/tracking/specTracker';

import { toValue } from '@vueuse/core';
import { computed, ref } from 'vue';

const specs = {
	/** Click on Add why to watch or "Next" button in empty WtW state. */
	modalOpened: { action: 'modal_opened' },

	modalClosed: { action: 'modal_closed' },

	/** Clicking the "next" button on the empty text recommendation form. */
	emptyNextClick: { action: 'modal_opened' },

	/** Click on Who do you recommend... */
	headlineFocused: { action: 'field_clicked', label: 'who' },

	/** "Who do you recommend..." field filled in then click outside. */
	headlineBlurred: {
		action: 'field_completed',
		label: 'who',
		triggerCheck: (headline: string) => headline.trim().length > 1,
	},

	/** Click on Why should someone... */
	bodyFocused: { action: 'field_clicked', label: 'why' },

	/** "Who should someone..." field filled in then click outside. */
	bodyBlurred: {
		action: 'field_completed',
		label: 'why',
		triggerCheck: (body: string) => body.trim().length > 60,
	},

	/** When the provider selector is clicked. */
	providerDropdownClicked: {
		action: 'dropdown_clicked',
		label: 'provider',
	},

	providerSelected: {
		action: 'provider_selected',
		label: 'provider',
	},

	nameFocused: {
		action: 'field_clicked',
		label: 'name',
	},

	nameBlurred: {
		action: 'field_completed',
		label: 'name',
		triggerCheck: (name: string) => name.trim().length > 0,
	},

	uploadImageClick: {
		action: 'upload_image_clicked',
		label: 'image',
	},

	imageSelected: {
		action: 'image_selected',
		label: 'image',
	},

	/** Submit the why to watch. */
	addWhyToWatch: {
		action: 'wtw_completed',
	},

	/** How many times the disabled button gets clicked. */
	disabledAddWhyToWatchClicked: {
		action: 'wtw_completed_fail_{number}',
		label: 'hello',
	},
} satisfies TrackingSpecs;

export function useWtwTracking(options: { title: MaybeRefOrGetter<Maybe<TitleDetail | PosterTitle>> }) {
	const titleContext = computed(() => {
		const title = toValue(options.title);
		if (title == null) return null;

		const objectId = title.__typename === 'Season' ? title.show.objectId : title.objectId;
		const objectType = title.__typename === 'Season' ? title.show.objectType : title.objectType;
		const seasonNumber = title.__typename === 'Season' ? title.content?.seasonNumber : undefined;

		return new SnowplowTitleContextGraphql(objectId, objectType, seasonNumber);
	});

	const trackWtw = createSpecTracker(specs, { property: 'wtw_recommendation' }, () => [titleContext.value]);

	const disableClickCount = ref(0);

	trackWtw.on('disabledAddWhyToWatchClicked', spec => {
		disableClickCount.value += 1;
		spec.action = spec.action.replace('{number}', `${disableClickCount.value}`);
	});

	return { trackWtw };
}

/** For OldTitledetail.vue which isn't CAPI. @deprecated */
export function trackModalOpened(contexts: SnowplowContext[]) {
	if (contexts.some(context => context == null)) return;

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'modal_opened',
			property: 'wtw_recommendation',
		},
		contexts as SnowplowContext[]
	);
}
